import React, { useState } from 'react';
import { api, STANDARD_STAGES, STAGE_LABELS, fmtMoney } from './constants';
import { GripVertical } from 'lucide-react';
import { toast } from 'sonner';

// Standard-lead Kanban board with native HTML5 drag & drop.
export default function KanbanBoard({ leads, onOpen, onChanged }) {
  const [dragId, setDragId] = useState(null);
  const [overCol, setOverCol] = useState(null);

  const standard = leads.filter(l => l.lead_type !== 'emergency');
  const byStage = (s) => standard.filter(l => (l.status || 'new') === s);

  const onDrop = async (stage) => {
    setOverCol(null);
    const id = dragId;
    setDragId(null);
    if (!id) return;
    const lead = standard.find(l => l.id === id);
    if (!lead || lead.status === stage) return;
    const res = await api.patch(`/leads/${id}`, { status: stage });
    if (res.ok) { toast.success(`Moved to ${STAGE_LABELS[stage]}`); onChanged?.(); }
    else toast.error('Move failed');
  };

  return (
    <div className="overflow-x-auto pb-4" data-testid="kanban-board">
      <div className="flex gap-3 min-w-max">
        {STANDARD_STAGES.map(stage => {
          const items = byStage(stage);
          return (
            <div key={stage}
              onDragOver={e => { e.preventDefault(); setOverCol(stage); }}
              onDragLeave={() => setOverCol(c => c === stage ? null : c)}
              onDrop={() => onDrop(stage)}
              data-testid={`kanban-col-${stage}`}
              className={`w-64 flex-shrink-0 rounded-lg border ${overCol === stage ? 'border-orange-500 bg-slate-800/80' : 'border-slate-700 bg-slate-800/40'} transition-colors`}>
              <div className="px-3 py-2.5 border-b border-slate-700 flex items-center justify-between sticky top-0">
                <span className="text-xs font-semibold text-slate-200">{STAGE_LABELS[stage]}</span>
                <span className="text-xs text-slate-500 bg-slate-900 px-1.5 rounded">{items.length}</span>
              </div>
              <div className="p-2 space-y-2 min-h-[120px] max-h-[calc(100vh-320px)] overflow-y-auto">
                {items.map(lead => (
                  <div key={lead.id} draggable
                    onDragStart={() => setDragId(lead.id)}
                    onDragEnd={() => setDragId(null)}
                    onClick={() => onOpen(lead.id)}
                    data-testid={`kanban-card-${lead.id}`}
                    className="bg-slate-900 border border-slate-700 rounded p-3 cursor-pointer hover:border-orange-500/50 group">
                    <div className="flex items-start justify-between gap-2">
                      <h4 className="text-sm font-medium text-white truncate">{lead.name}</h4>
                      <GripVertical className="w-4 h-4 text-slate-600 group-hover:text-slate-400 flex-shrink-0" />
                    </div>
                    <p className="text-xs text-slate-400 truncate mt-0.5">{lead.services?.join(', ') || lead.description || '—'}</p>
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-[10px] text-slate-500">{lead.source}</span>
                      {lead.estimated_value && <span className="text-[10px] text-green-400">{fmtMoney(lead.estimated_value.replace(/[^0-9.]/g, ''))}</span>}
                    </div>
                  </div>
                ))}
                {!items.length && <div className="text-center text-slate-600 text-xs py-6">Drop here</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
